import React, { Fragment, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTable, useRowSelect, useExpanded } from 'react-table'
import TableRow from './TableRow'
import ContextMenu from '../UI/ContextMenu'
import AlertLabel from '../UI/AlertLabel'
import useWindowSize from '../../hooks/useWindowSize'
import SearchBar from '../UI/SearchBar'
import InfiniteScroll from 'react-infinite-scroll-component'
import { TableProps } from '../../types/TableProps'
import { prepareFilters } from '../../utils/prepareFilters'
import { filtersOptions } from '../../utils/filtersOptions'

const SearchBarWrapper = styled.div`
  position: relative;
  padding-inline: 20px;
  z-index: 10;
`

const TableStyle = styled.div<{ height?: string }>`
  padding-inline: 20px 10px;
  //padding-top: 20px;
  overflow: ${({ height }) => (height === '100%' ? 'unset' : 'auto')};
  height: ${({ height }) => height};
  position: relative;

  table {
    //overflow: hidden;
    position: relative;
    //margin-top: -20px;
    width: 100%;
    font-size: 12px;
    border-collapse: separate;
    border-spacing: 0 5px;

    th,
    td {
      display: table-cell;
      border: transparent;
      margin: 0;
      padding: 1rem;

      :last-child {
        border-right: 0;
      }
    }

    th {
      font-weight: normal;
    }
  }
  thead {
    th {
      z-index: 1;
      position: sticky;
      top: 0;
      background: #eff2f5;

      padding-block: 8px;
    }
  }

  tbody {
    tr {
      display: table-row;
      vertical-align: inherit;
      border-color: inherit;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
      border-radius: 5px;
    }
  }

  td {
    padding: 5px;
    background-color: #ffffff;
  }

  td:first-child {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  td:last-child {
    border-bottom-right-radius: 5px;
    border-top-right-radius: 5px;
    padding: 0;
  }

  .infinite-scroll-component {
    overflow: unset !important;
  }

  .pagination {
    padding: 0.5rem;
  }
`

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }: any, ref) => {
    const defaultRef = React.useRef()
    const resolvedRef: any = ref || defaultRef

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])

    return (
      <Fragment>
        <input type="checkbox" ref={resolvedRef} {...rest} />
      </Fragment>
    )
  }
)

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox'

function TableFunc({
  columns,
  data,
  isMultiSelect,
  fetchMoreData,
  hasMore,
  scrollableTarget,
}: any) {
  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    visibleColumns,
  }: // selectedFlatRows,
  // state: { selectedRowIds },
  any = useTable(
    {
      columns,
      data,
    },
    useExpanded,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          accessor: 'alertLabel',
          style: {
            padding: '0 10px 0 0',
          },
          Header: () => null,
          Cell: function label(value) {
            return <AlertLabel {...value.row.original} />
          },
        },
        {
          id: 'selection',
          style: {
            display: `${!isMultiSelect && 'none'}`,
          },
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: function Header({ getToggleAllRowsSelectedProps }: any) {
            return (
              <IndeterminateCheckbox
                {...getToggleAllRowsSelectedProps()}
              />
            )
          },

          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: function Cell({ row }: any) {
            return (
              <div>
                <IndeterminateCheckbox
                  {...row.getToggleRowSelectedProps()}
                />
              </div>
            )
          },
        },

        ...columns,
      ])
    }
  )

  // const [isExpand, setIsExpand] = useState()

  // Render the UI for your table
  return (
    <Fragment>
      <InfiniteScroll
        dataLength={rows.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={<h4>Loading...</h4>}
        scrollableTarget={scrollableTarget}
      >
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: any, key) => (
              <tr key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any, key) => (
                  <th
                    key={key}
                    {...column.getHeaderProps([
                      {
                        className: column.className,
                        style: column.style,
                      },
                    ])}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, key) => {
              prepareRow(row)
              return (
                <TableRow
                  visibleColumns={visibleColumns}
                  key={key}
                  rowData={row}
                  id={key}
                />
              )
            })}
          </tbody>
        </table>
      </InfiniteScroll>
    </Fragment>
  )
}

export type TableInfiniteScrollProps = {
  chunkSize?: number
} & TableProps

export const TableInfiniteScroll: React.FC<TableInfiniteScrollProps> = ({
  chunkSize = 20,
  onFilter,
  actions,
  onAction,
  isMultiSelect = false,
  isExpandable,
  styles,
  height100,
  data,
}) => {
  const columns = React.useMemo(
    () => [
      {
        id: 'expander', // Make sure it has an ID
        style: {
          padding: 0,
        },
        Header: () => null,
        Cell: function expandSubComponent({ row }) {
          return isExpandable ? (
            <span {...row.getToggleRowExpandedProps()}>
              {row.isExpanded ? '▼' : '►'}
            </span>
          ) : null
        },
      },
      ...data.metadata.columns,
    ],
    []
  )
  const filters = prepareFilters(data.metadata.columns)
  const filterOptions = filtersOptions(filters, data.data)

  actions &&
    onAction &&
    columns.push({
      id: 'contextMenu',
      Header: () => null,
      Cell: function contextMenu({ row }) {
        return (
          <ContextMenu
            actions={actions}
            onAction={onAction}
            id={row.values.id}
          />
        )
      },
    })
  const { height } = useWindowSize()
  const tableHeight = height100 ? '100%' : `${height - 373}px`
  const [currentIndex, setCurrentIndex] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  const chunkArray = (dataArray, chunkSize) => {
    const results: any = []

    for (let i = 0; i < dataArray.length; i += chunkSize) {
      results.push(dataArray.slice(i, i + chunkSize))
    }
    return results
  }

  const result = chunkArray(data.data, chunkSize)
  const [items, setItems] = useState(result[0])

  useEffect(() => {
    setHasMore(result[currentIndex]?.length === chunkSize)
  }, [currentIndex])

  //dummy timeout
  const fetchMoreData = () => {
    setTimeout(() => {
      setItems(items.concat(result[currentIndex + 1]))
    }, 1500)
    setCurrentIndex(currentIndex + 1)
  }

  const dataFinal = React.useMemo(() => items, [items])

  return (
    <Fragment>
      <SearchBarWrapper style={styles?.searchBarStyles}>
        <SearchBar
          onFilter={onFilter}
          filtersSet={filters}
          filterOptions={filterOptions}
        />
      </SearchBarWrapper>
      <TableStyle
        id="scrollableDiv"
        height={tableHeight}
        style={styles?.tablesStyles}
      >
        <TableFunc
          isMultiSelect={isMultiSelect}
          columns={columns}
          data={dataFinal}
          scrollableTarget={height100 ? '' : 'scrollableDiv'}
          fetchMoreData={fetchMoreData}
          hasMore={hasMore}
        />
      </TableStyle>
    </Fragment>
  )
}

export default TableInfiniteScroll
